import {useState} from 'react';
import {commitTicketingMutation} from '../../../graphql/forecastRelay';
import CreateForecastTaskMutation from '../../../../../mutations/ticketing/tickets/createForecastTaskMutation';

export interface CreateForecastTaskInput {
	name: string;
	projectId: string;
	assignedPersonIds?: string[] | null;
	dueDate?: string | null; // YYYY-MM-DD
	description?: string | null;
}

// Split a YYYY-MM-DD string into Forecast's deadline Y/M/D triple. Kept local to
// the ticketing island: shared/util/DateUtil transitively pulls the
// design-system graph, which the island deliberately avoids (and which breaks
// the stubbed jest env — see memory web-front-jest-design-system-stub-failures).
function deadlinePartsFromIsoDate(isoDate: string | null | undefined): {
	year: number | null;
	month: number | null;
	day: number | null;
} {
	const m = isoDate ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate) : null;
	if (!m) return {year: null, month: null, day: null};
	return {year: Number(m[1]), month: Number(m[2]), day: Number(m[3])};
}

// service-company reports validation failures as bare enum names (e.g.
// TASK_PROJECT_REQUIRED), which the GraphQL layer forwards verbatim into
// `errors`. Translate the ones this flow can actually provoke; anything else
// falls through unchanged so an unmapped code is still visible rather than
// swallowed.
const CREATE_TASK_ERROR_MESSAGES: Record<string, string> = {
	TASK_PROJECT_REQUIRED: 'A project is required to create a task.',
	TASK_NAME_TOO_LONG: 'That task name is too long.',
	PROJECT_ID_NOT_FOUND: 'That project no longer exists.',
	TASK_DEADLINE_DATE_INVALID: 'That due date is not a valid date.',
};

function createTaskErrorMessage(errors: string[]): string {
	return errors.map(e => CREATE_TASK_ERROR_MESSAGES[e.trim()] ?? e).join(' ');
}

export interface CreatedForecastTask {
	id: string;
	companyTaskId: number | null;
	name: string | null;
}

export function useCreateForecastTask() {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	function submit(input: CreateForecastTaskInput): Promise<CreatedForecastTask> {
		setError(null);
		setLoading(true);
		const {year, month, day} = deadlinePartsFromIsoDate(input.dueDate);
		const mutationInput = {
			name: input.name,
			projectId: input.projectId,
			// Send [] rather than null when no assignee is chosen: an explicit null
			// crashes service-company's TaskValidationLogic (isAssignedPersonsSet
			// becomes true while the value is null → NPE → 500 on /task).
			assignedPersons: input.assignedPersonIds && input.assignedPersonIds.length > 0 ? input.assignedPersonIds : [],
			description: input.description || null,
			deadlineYear: year,
			deadlineMonth: month,
			deadlineDay: day,
		};
		return new Promise((resolve, reject) => {
			commitTicketingMutation(
				CreateForecastTaskMutation,
				mutationInput,
				response => {
					setLoading(false);
					// CreateTask reports validation failures in-band: it resolves with a
					// populated `errors` array and a null task rather than raising. Read
					// `errors` first so the user sees the actual reason instead of the
					// generic "returned no task" fallback.
					const returnedErrors = (response.createTask?.errors ?? []).filter(
						(e): e is string => typeof e === 'string' && e.length > 0
					);
					const node = response.createTask?.task?.node;
					if (!node) {
						const msg =
							returnedErrors.length > 0
								? createTaskErrorMessage(returnedErrors)
								: 'Task creation returned no task';
						setError(msg);
						reject(new Error(msg));
						return;
					}
					resolve({id: node.id, companyTaskId: node.companyTaskId ?? null, name: node.name ?? null});
				},
				err => {
					setLoading(false);
					setError(err instanceof Error ? err.message : String(err));
					reject(err);
				}
			);
		});
	}

	return {submit, loading, error};
}
